import type { BlogArticle, BlogFaqItem } from '~/types/blog'

/**
 * AIEO (Article Integrity & Editorial Oversight) validation result
 * Provides structured assessment of article completeness against editorial checklist
 */
export interface AieoValidationResult {
  /** Overall validation status - true if no critical violations */
  passed: boolean
  /** Timestamp of validation */
  timestamp: number
  /** Severity level of worst violation: 'error' | 'warning' | 'info' */
  severity: 'error' | 'warning' | 'info'
  /** Array of specific violations found */
  violations: AieoViolation[]
  /** Aggregated coverage score (0-100) */
  coverageScore: number
  /** Detailed breakdown of each check */
  checklist: AieoChecklistItem[]
}

/**
 * Individual violation with context for remediation
 */
export interface AieoViolation {
  code: string
  severity: 'error' | 'warning' | 'info'
  field: string
  message: string
  remediation: string
  value?: unknown
}

/**
 * Individual checklist item with pass/fail state
 */
export interface AieoChecklistItem {
  code: string
  label: string
  passed: boolean
  severity: 'error' | 'warning' | 'info'
  detail?: string
  value?: unknown
}

/**
 * AIEO checklist configuration - thresholds for different checks
 */
const AIEO_CHECKLIST_CONFIG = {
  faqMinCount: 3,
  faqMaxCount: 10,
  evidenceSourcesMinCount: 2,
  authorExpertiseRequired: true,
  readingTimeRequired: true,
  featuredImageRequired: true,
  excerptRequired: true,
  questionHeadersMinPercentage: 20, // at least 20% of headers should be questions
}

/**
 * Validate content body for question-based headers
 * Returns count of headers containing question marks
 */
function countQuestionHeaders(body: unknown): { total: number; questions: number } {
  if (!body || typeof body !== 'object') {
    return { total: 0, questions: 0 }
  }

  const record = body as Record<string, unknown>
  const children = Array.isArray(record.children) ? record.children : []

  let totalHeadings = 0
  let questionHeadings = 0

  function traverse(node: unknown): void {
    if (!node || typeof node !== 'object') return

    const n = node as Record<string, unknown>
    const tag = n.tag as string | undefined

    // Count h2, h3 headers
    if (tag === 'h2' || tag === 'h3' || tag === 'h4') {
      totalHeadings += 1
      const text = extractNodeText(n).toLowerCase()
      if (text.includes('?') || text.includes('faq') || text.includes('frequently')) {
        questionHeadings += 1
      }
    }

    // Traverse children
    if (Array.isArray(n.children)) {
      for (const child of n.children) {
        traverse(child)
      }
    }
  }

  for (const child of children) {
    traverse(child)
  }

  return { total: totalHeadings, questions: questionHeadings }
}

/**
 * Extract text content from a node tree
 */
function extractNodeText(node: unknown): string {
  if (typeof node === 'string') return node
  if (Array.isArray(node)) return node.map(extractNodeText).join(' ')
  if (!node || typeof node !== 'object') return ''

  const record = node as Record<string, unknown>
  if (typeof record.value === 'string') return record.value
  if (Array.isArray(record.children)) return extractNodeText(record.children)

  return ''
}

/**
 * Main AIEO validation composable
 * Validates article structure against editorial checklist
 *
 * Usage:
 *   const { validateArticle } = useAieoValidation()
 *   const result = validateArticle(article, documentBody)
 */
export function useAieoValidation() {
  /**
   * Validate a complete blog article against AIEO checklist
   *
   * @param article - BlogArticle object with metadata
   * @param documentBody - Content body AST (optional)
   * @returns AieoValidationResult with full assessment
   */
  function validateArticle(
    article: BlogArticle | null,
    documentBody?: unknown,
  ): AieoValidationResult {
    const violations: AieoViolation[] = []
    const checklist: AieoChecklistItem[] = []

    if (!article) {
      return {
        passed: false,
        timestamp: Date.now(),
        severity: 'error',
        violations: [
          {
            code: 'ARTICLE_NULL',
            severity: 'error',
            field: 'article',
            message: 'Article object is null or undefined',
            remediation: 'Ensure article data is loaded before validation',
          },
        ],
        coverageScore: 0,
        checklist: [],
      }
    }

    // Check 1: FAQ Items (3-5 minimum)
    const faqCount = article.faq?.length ?? 0
    const faqPassed = faqCount >= AIEO_CHECKLIST_CONFIG.faqMinCount
    checklist.push({
      code: 'FAQ_ITEMS',
      label: 'FAQ Items (minimum 3)',
      passed: faqPassed,
      severity: faqCount === 0 ? 'error' : faqCount < 3 ? 'warning' : 'info',
      detail: `Found ${faqCount} FAQ items`,
      value: faqCount,
    })

    if (!faqPassed) {
      violations.push({
        code: 'FAQ_INSUFFICIENT',
        severity: faqCount === 0 ? 'error' : 'warning',
        field: 'faq',
        message: `Article has ${faqCount} FAQ items, but ${AIEO_CHECKLIST_CONFIG.faqMinCount} minimum required`,
        remediation: `Add ${Math.max(0, AIEO_CHECKLIST_CONFIG.faqMinCount - faqCount)} more FAQ question-answer pairs to the article`,
        value: faqCount,
      })
    }

    // Check 2: Evidence Sources (2-3 minimum)
    const evidenceCount = article.evidenceSources?.length ?? 0
    const evidencePassed = evidenceCount >= AIEO_CHECKLIST_CONFIG.evidenceSourcesMinCount
    checklist.push({
      code: 'EVIDENCE_SOURCES',
      label: 'Evidence Sources (minimum 2)',
      passed: evidencePassed,
      severity: evidenceCount === 0 ? 'error' : evidenceCount < 2 ? 'warning' : 'info',
      detail: `Found ${evidenceCount} evidence sources`,
      value: evidenceCount,
    })

    if (!evidencePassed) {
      violations.push({
        code: 'EVIDENCE_INSUFFICIENT',
        severity: evidenceCount === 0 ? 'error' : 'warning',
        field: 'evidenceSources',
        message: `Article has ${evidenceCount} evidence sources, but ${AIEO_CHECKLIST_CONFIG.evidenceSourcesMinCount} minimum required`,
        remediation: `Add ${Math.max(0, AIEO_CHECKLIST_CONFIG.evidenceSourcesMinCount - evidenceCount)} more authoritative sources (links to research, studies, documentation)`,
        value: evidenceCount,
      })
    }

    // Check 3: Author Expertise/Credentials
    const hasExpertise = Boolean(
      article.author &&
        (article.author.expertise || article.author.title) &&
        (article.author.expertise?.trim().length ?? 0) > 0 ||
        (article.author.title?.trim().length ?? 0) > 0,
    )
    checklist.push({
      code: 'AUTHOR_EXPERTISE',
      label: 'Author Expertise/Credentials',
      passed: hasExpertise,
      severity: hasExpertise ? 'info' : 'warning',
      detail: hasExpertise
        ? `Author credentials: ${article.author.expertise || article.author.title}`
        : 'Author expertise/credentials not documented',
      value: article.author.expertise || article.author.title,
    })

    if (!hasExpertise) {
      violations.push({
        code: 'AUTHOR_MISSING_EXPERTISE',
        severity: 'warning',
        field: 'author.expertise',
        message: 'Author expertise or credentials are not documented',
        remediation:
          'Add author expertise/credentials in frontmatter (e.g., "Senior Software Engineer" or "10+ years in Cloud Architecture")',
        value: article.author,
      })
    }

    // Check 4: Reading Time Estimate
    const hasReadingTime = article.readingTime > 0
    checklist.push({
      code: 'READING_TIME',
      label: 'Reading Time Estimate',
      passed: hasReadingTime,
      severity: hasReadingTime ? 'info' : 'error',
      detail: `${article.readingTime} min read`,
      value: article.readingTime,
    })

    if (!hasReadingTime) {
      violations.push({
        code: 'READING_TIME_MISSING',
        severity: 'error',
        field: 'readingTime',
        message: 'Reading time estimate is missing or zero',
        remediation:
          'Ensure reading_time is set in article frontmatter or computed from content length (typically 200 words per minute)',
        value: article.readingTime,
      })
    }

    // Check 5: Featured/Hero Image
    const hasImage = Boolean(article.heroImage?.trim())
    checklist.push({
      code: 'FEATURED_IMAGE',
      label: 'Featured Image',
      passed: hasImage,
      severity: hasImage ? 'info' : 'warning',
      detail: hasImage ? (article.heroImage ?? '') : 'No featured image',
      value: article.heroImage ?? undefined,
    })

    if (!hasImage) {
      violations.push({
        code: 'FEATURED_IMAGE_MISSING',
        severity: 'warning',
        field: 'heroImage',
        message: 'Article does not have a featured/hero image',
        remediation:
          'Add a hero_image in the article frontmatter (path to image asset, recommended 1200x630px minimum)',
        value: article.heroImage,
      })
    }

    // Check 6: Article Excerpt
    const hasExcerpt = Boolean(article.excerpt?.trim())
    checklist.push({
      code: 'EXCERPT',
      label: 'Article Excerpt',
      passed: hasExcerpt,
      severity: hasExcerpt ? 'info' : 'warning',
      detail: hasExcerpt
        ? `Excerpt: "${article.excerpt.substring(0, 60)}..."`
        : 'No excerpt provided',
      value: article.excerpt,
    })

    if (!hasExcerpt) {
      violations.push({
        code: 'EXCERPT_EMPTY',
        severity: 'warning',
        field: 'excerpt',
        message: 'Article excerpt is empty or missing',
        remediation:
          'Add a brief summary (1-2 sentences, ~100 chars) as the article excerpt for preview in lists',
        value: article.excerpt,
      })
    }

    // Check 7: Question-Based Headers (heuristic)
    const headerAnalysis = countQuestionHeaders(documentBody)
    const questionsPercentage =
      headerAnalysis.total > 0 ? Math.round((headerAnalysis.questions / headerAnalysis.total) * 100) : 0
    const questionHeadersPassed =
      headerAnalysis.total === 0 || questionsPercentage >= AIEO_CHECKLIST_CONFIG.questionHeadersMinPercentage
    checklist.push({
      code: 'QUESTION_HEADERS',
      label: 'Question-Based Headers (20%+ of headers)',
      passed: questionHeadersPassed,
      severity: questionHeadersPassed ? 'info' : 'info', // informational, not enforced
      detail: `${headerAnalysis.questions}/${headerAnalysis.total} headers contain questions (${questionsPercentage}%)`,
      value: { questions: headerAnalysis.questions, total: headerAnalysis.total },
    })

    // Calculate coverage score (0-100)
    const passedChecks = checklist.filter(item => item.passed).length
    const coverageScore = Math.round((passedChecks / checklist.length) * 100)

    // Determine overall pass/fail and severity
    const criticalViolations = violations.filter(v => v.severity === 'error')
    const hasErrors = criticalViolations.length > 0
    const hasSeverity = violations.length > 0

    const result: AieoValidationResult = {
      passed: !hasErrors,
      timestamp: Date.now(),
      severity: hasErrors ? 'error' : hasSeverity ? 'warning' : 'info',
      violations,
      coverageScore,
      checklist,
    }

    return result
  }

  /**
   * Format validation result for console logging
   */
  function formatValidationForLog(result: AieoValidationResult): string {
    const lines: string[] = [
      `\n${'='.repeat(60)}`,
      `AIEO ARTICLE VALIDATION REPORT`,
      `${'='.repeat(60)}`,
      `Status: ${result.passed ? '✓ PASSED' : '✗ FAILED'} (${result.severity.toUpperCase()})`,
      `Coverage Score: ${result.coverageScore}%`,
      `Timestamp: ${new Date(result.timestamp).toISOString()}`,
      `\nCHECKLIST (${result.checklist.filter(c => c.passed).length}/${result.checklist.length} passed):`,
      ...result.checklist.map(
        item =>
          `  ${item.passed ? '✓' : '✗'} ${item.label}${item.detail ? ` — ${item.detail}` : ''}`,
      ),
    ]

    if (result.violations.length > 0) {
      lines.push(`\nVIOLATIONS (${result.violations.length}):`)
      for (const v of result.violations) {
        lines.push(`\n  [${v.severity.toUpperCase()}] ${v.code}`)
        lines.push(`    Field: ${v.field}`)
        lines.push(`    Message: ${v.message}`)
        lines.push(`    Remediation: ${v.remediation}`)
      }
    }

    lines.push(`\n${'='.repeat(60)}\n`)
    return lines.join('\n')
  }

  return {
    validateArticle,
    formatValidationForLog,
    AIEO_CHECKLIST_CONFIG,
  }
}
